import { GRID_CONFIG, sortByAuditUpdatedOn } from "../config";
// src/pages/FAQ_Admin.js
import axios from "axios";
import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import {
    FaPlusCircle,
    FaPencilAlt,
    FaTrash,
    FaAngleDoubleLeft,
    FaAngleLeft,
    FaAngleRight,
    FaAngleDoubleRight,
    FaSearch,
} from "react-icons/fa";
import Swal from "sweetalert2";
import "react-toastify/dist/ReactToastify.css";

import { notifyUser } from "../utils/notifyUser";

const FAQ_Admin = () => {
    const [faqs, setFaqs] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [currentFAQ, setCurrentFAQ] = useState({
        question: "",
        answer: "",
        role: "General",
    });
    const [perPage, setPerPage] = useState(GRID_CONFIG.defaultPageSize);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedFAQ, setSelectedFAQ] = useState(null);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [roleFilter, setRoleFilter] = useState("");

    // Debounce search term to prevent excessive API calls
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => {
            clearTimeout(timerId);
        };
    }, [searchTerm]);

    // Fetch paginated FAQs whenever page, perPage or search term changes
    useEffect(() => {
        fetchPaginatedFAQs();
    }, [currentPage, perPage, debouncedSearchTerm, roleFilter]);

    const fetchPaginatedFAQs = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/faq/paginated/all`,
                {
                    params: {
                        page: currentPage,
                        limit: perPage,
                        search: debouncedSearchTerm,
                        role: roleFilter,
                    },
                },
            );

            setFaqs(sortByAuditUpdatedOn(response.data.data));
            setTotalRecords(response.data.pagination.totalItems);
            setTotalPages(response.data.pagination.totalPages);
        } catch{
            notifyUser("faq-fetch-error", "error", "Failed to fetch FAQs", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setLoading(false);
        }
    };

    // Legacy method for backward compatibility
    // const fetchFAQs = async () => {
    //     try {
    //         setLoading(true);
    //         const response = await axios.get(
    //             `${process.env.REACT_APP_BACKEND_URL}/api/faq/all`
    //         );
    //         setFaqs(response.data);
    //     } catch{
    //         toast.error("Failed to fetch FAQs", { toastId: "faq-fetch-error" });
    //         toast.error("Failed to fetch FAQs", { toastId: "faq-fetch-error" });
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const handleExportFAQsCSV = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/faq/all`,
                {
                    params: {
                        search: debouncedSearchTerm,
                        role: roleFilter,
                    },
                },
            );

            const data = response.data;
            if (!data || data.length === 0) {
                notifyUser(null, "info", "No FAQs to export", "toast", null, null, null, null, null, false, false, "top-center");
                return;
            }

            const rows = data.map((f) => ({
                Question: f.question || "",
                Answer: f.answer || "",
                Role: f.role || "",
                CreatedAt: f.created_at
                    ? new Date(f.created_at).toLocaleString()
                    : "",
                UpdatedAt: f.updated_at
                    ? new Date(f.updated_at).toLocaleString()
                    : "",
            }));

            const header = Object.keys(rows[0]).join(",");
            const csvBody = rows
                .map((r) =>
                    Object.values(r)
                        .map((v) => `"${String(v).replace(/\"/g, '""')}"`)
                        .join(","),
                )
                .join("\n");

            const csv = `${header}\n${csvBody}`;
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                `faqs-${new Date().toISOString().slice(0, 10)}.csv`,
            );
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            notifyUser(null, "error", "Failed to export FAQs", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };
    // Total pages is now provided by the server
    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "You are about to delete this FAQ.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!",
        });
        if(result.isConfirmed) {
            try {
                await axios.delete(
                    `${process.env.REACT_APP_BACKEND_URL}/api/faq/${id}`,
                );
                notifyUser(null, "success", "FAQ deleted", "toast", null, null, null, null, null, false, false, "top-center");
                setSelectedFAQ(null);
                fetchPaginatedFAQs();
            } catch{
                notifyUser(null, "error", "Failed to delete FAQ", "toast", null, null, null, null, null, false, false, "top-center");
            }
        }
    };

    const handleSubmit = async () => {
        try {
            if(isEditing) {
                await axios.put(
                    `${process.env.REACT_APP_BACKEND_URL}/api/faq/${currentFAQ.id}`,
                    currentFAQ,
                );
                notifyUser(null, "success", "FAQ updated", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/api/faq`,
                    currentFAQ,
                );
                notifyUser(null, "success", "FAQ created", "toast", null, null, null, null, null, false, false, "top-center");
            }
            setModalOpen(false);
            setSelectedFAQ(null);
            fetchPaginatedFAQs();
        } catch{
            notifyUser(null, "error", "Failed to save FAQ", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const columns = [
        {
            name: "Question",
            selector: (row) => row.question,
            sortable: true,
            grow: 1,
        },
        {
            name: "Answer",
            selector: (row) => row.answer,
            sortable: false,
            grow: 1,
            cell: (row) => (
                <div
                    style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "400px",
                        cursor: "pointer",
                    }}
                    title={row.answer}
                >
                    {row.answer}
                </div>
            ),
        },

        {
            name: "Role",
            selector: (row) => row.role,
            sortable: true,
            width: "120px",
        },
        {
            name: "Created At",
            selector: (row) => row.created_at,
            sortable: true,
            width: "160px",
            cell: (row) => {
                try {
                    // First check if date is valid
                    const date = new Date(row.created_at);
                    return date instanceof Date && !isNaN(date)
                        ? date.toLocaleDateString()
                        : "N/A";
                } catch{
                    return "N/A";
                }
            },
        },
        {
            name: "Updated At",
            selector: (row) => row.updated_at,
            sortable: true,
            width: "160px",
            cell: (row) => {
                try {
                    const date = new Date(row.updated_at);
                    return date instanceof Date && !isNaN(date)
                        ? date.toLocaleDateString()
                        : "N/A";
                } catch{
                    return "N/A";
                }
            },
        },
    ];

    const customStyles = {
        table: {
            style: {
                borderRadius: "8px",
                overflow: "hidden",
            },
        },
        headRow: {
            style: {
                backgroundColor: "var(--navy)",
                color: "white",
                fontWeight: "bold",
                fontSize: "16px",
                minHeight: "50px",
            },
        },
        headCells: {
            style: {
                paddingLeft: "25px",
                paddingRight: "25px",
            },
        },
        cells: {
            style: {
                paddingLeft: "25px",
                paddingRight: "25px",
                fontSize: "14px",
            },
        },
        rows: {
            style: {
                cursor: "pointer",
                minHeight: "45px",
                "&:hover": {
                    backgroundColor: "#f1f8ff",
                },
            },
            stripedStyle: {
                backgroundColor: "#F5F7FA",
            },
        },
    };

    const conditionalRowStyles = [
        {
            when: (row) => selectedFAQ?.id === row.id,
            style: {
                backgroundColor: "#e5e7eb",
                borderLeft: "4px solid var(--navy)",
                fontWeight: "600",
            },
        },
    ];

    return (
        <div
            className="aqi-grid-page"
            style={{
                padding: "30px",
                width: "90%",
                margin: "30px auto",
                boxShadow: "0 0 20px rgba(0,0,0,0.1)",
                borderRadius: "8px",
                backgroundColor: "white",
                position: "relative",
            }}
        >
            <h2
                style={{
                    color: "var(--navy)",
                    marginBottom: "20px",
                    fontSize: "large",
                    fontFamily: "large",
                }}
            >
                FAQ MANAGEMENT
            </h2>

            {/* Controls */}
            <div className="aqi-grid-toolbar">
                {/* Records per page dropdown */}
                <div>
                        <select
                            style={{
                                padding: "0.375rem 2rem 0.375rem 0.75rem",
                                color: "var(--navy)",
                                backgroundColor: "#F5F7FA",
                                border: "2px solid #C2D7E2",
                                borderRadius: "0.375rem",
                                fontWeight: "500",
                                appearance: "none",
                                backgroundImage:
                                    "var(--navy-select-chevron)",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "right 0.5rem center",
                                backgroundSize: "16px 12px",
                                transition: "all 0.2s ease",
                                cursor: "pointer",
                                outline: "none",
                            }}
                            className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                            value={perPage}
                            onChange={(e) => {
                                setPerPage(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                        >
                            {GRID_CONFIG.pageSizeOptions.map((option) => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                </div>

                {/* Role Filter dropdown */}
                <div>
                        <select
                            style={{
                                padding: "0.375rem 2rem 0.375rem 0.75rem",
                                color: "var(--navy)",
                                backgroundColor: "#F5F7FA",
                                border: "2px solid #C2D7E2",
                                borderRadius: "0.375rem",
                                fontWeight: "500",
                                appearance: "none",
                                backgroundImage:
                                    "var(--navy-select-chevron)",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "right 0.5rem center",
                                backgroundSize: "16px 12px",
                                transition: "all 0.2s ease",
                                cursor: "pointer",
                                outline: "none",
                                minWidth: "180px",
                            }}
                            className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                            value={roleFilter}
                            onChange={(e) => {
                                setRoleFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                        >
                            <option value="">All Roles</option>
                            <option value="General">General</option>
                            <option value="Investor">Investor</option>
                            <option value="Entrepreneur">Entrepreneur</option>
                            <option value="glossary">Glossary</option>
                        </select>
                </div>

                <div className="aqi-grid-search">
                            <FaSearch
                                className="absolute left-3 top-2.5"
                                style={{ color: "var(--navy)" }}
                            />
                            <input
                                type="text"
                                placeholder="Search FAQs..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                                style={{
                                    paddingLeft: "2.5rem",
                                    paddingRight: "0.75rem",
                                    paddingTop: "0.5rem",
                                    paddingBottom: "0.5rem",
                                    borderRadius: "0.375rem",
                                    border: "2px solid #C2D7E2",
                                    backgroundColor: "#F5F7FA",
                                    transition: "all 0.2s ease",
                                    outline: "none",
                                    color: "var(--navy)",
                                }}
                                className="aqi-grid-search-input hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                            />
                            {loading && searchTerm && (
                                <span className="text-xs text-gray-500 ml-2">
                                    Searching...
                                </span>
                            )}
                </div>

                <div className="aqi-grid-toolbar-actions aqi-grid-toolbar-actions--export-last">
                    <button
                        className="aqi-grid-button aqi-grid-button--primary"
                        onClick={() => {
                            setModalOpen(true);
                            setIsEditing(false);
                            setCurrentFAQ({
                                question: "",
                                answer: "",
                                role: "General",
                            });
                        }}
                    >
                        <FaPlusCircle /> Create
                    </button>
                    {selectedFAQ && (
                        <>
                            <button
                                className="aqi-grid-button aqi-grid-button--primary"
                                onClick={() => {
                                    setIsEditing(true);
                                    setCurrentFAQ(selectedFAQ);
                                    setModalOpen(true);
                                }}
                            >
                                <FaPencilAlt /> Edit
                            </button>
                            <button
                                className="aqi-grid-button aqi-grid-button--danger"
                                onClick={() => handleDelete(selectedFAQ.id)}
                            >
                                <FaTrash /> Delete
                            </button>
                        </>
                    )}
                    <button
                        onClick={handleExportFAQsCSV}
                        title="Export CSV"
                        className="aqi-grid-button aqi-grid-button--primary"
                    >
                        Export
                    </button>
                </div>
            </div>

            {/* Table */}
            <DataTable
                columns={columns}
                data={faqs}
                customStyles={customStyles}
                conditionalRowStyles={conditionalRowStyles}
                progressPending={loading}
                pagination={false}
                onRowClicked={(row) =>
                    setSelectedFAQ((prev) =>
                        prev?.id === row.id ? null : row,
                    )
                }
                highlightOnHover
                pointerOnHover
                responsive
                striped
            />

            {/* Pagination */}
            <div className="flex justify-between items-center px-2 py-3 text-sm text-gray-700 mt-4">
                <span>
                    Showing{" "}
                    {totalRecords === 0 ? 0 : (currentPage - 1) * perPage + 1}{" "}
                    to {Math.min(currentPage * perPage, totalRecords)} of{" "}
                    {totalRecords} records
                </span>
                <div className="flex gap-2 text-lg items-center">
                    <button
                        disabled={currentPage === 1 || loading}
                        onClick={() => setCurrentPage(1)}
                        className="disabled:opacity-40"
                    >
                        <FaAngleDoubleLeft />
                    </button>
                    <button
                        disabled={currentPage === 1 || loading}
                        onClick={() =>
                            setCurrentPage((prev) => Math.max(prev - 1, 1))
                        }
                        className="disabled:opacity-40"
                    >
                        <FaAngleLeft />
                    </button>
                    <span className="text-sm px-2">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        disabled={currentPage === totalPages || loading}
                        onClick={() =>
                            setCurrentPage((prev) =>
                                Math.min(prev + 1, totalPages),)
                        }
                        className="disabled:opacity-40"
                    >
                        <FaAngleRight />
                    </button>
                    <button
                        disabled={currentPage === totalPages || loading}
                        onClick={() => setCurrentPage(totalPages)}
                        className="disabled:opacity-40"
                    >
                        <FaAngleDoubleRight />
                    </button>
                </div>
            </div>

            {/* Modal */}
            {modalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 ">
                    <div className="bg-white p-8 rounded-lg shadow-lg w-[90%] sm:w-[800px]">
                        <h3 className="text-xl font-semibold mb-4 text-navy">
                            {isEditing ? "Edit FAQ" : "Create FAQ"}
                        </h3>
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleSubmit();
                            }}
                            className="flex flex-col gap-4 "
                        >
                            <input
                                type="text"
                                placeholder="Question"
                                value={currentFAQ.question}
                                onChange={(e) =>
                                    setCurrentFAQ({
                                        ...currentFAQ,
                                        question: e.target.value,
                                    })
                                }
                                className="p-3 border-2 border-[#C2D7E2] rounded hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:outline-none"
                                required
                            />
                            <textarea
                                placeholder="Answer"
                                value={currentFAQ.answer}
                                onChange={(e) =>
                                    setCurrentFAQ({
                                        ...currentFAQ,
                                        answer: e.target.value,
                                    })
                                }
                                className="p-3 border-2 border-[#C2D7E2] rounded hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:outline-none"
                                style={{ height: "300px" }}
                            ></textarea>
                            <select
                                value={currentFAQ.role}
                                onChange={(e) =>
                                    setCurrentFAQ({
                                        ...currentFAQ,
                                        role: e.target.value,
                                    })
                                }
                                className="p-3 border-2 border-[#C2D7E2] rounded hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:outline-none"
                                style={{
                                    appearance: "none",
                                    backgroundImage:
                                        "var(--navy-select-chevron)",
                                    backgroundRepeat: "no-repeat",
                                    backgroundPosition: "right 0.5rem center",
                                    backgroundSize: "16px 12px",
                                    paddingRight: "2rem",
                                }}
                            >
                                <option value="General">General</option>
                                <option value="Investor">Investor</option>
                                <option value="Entrepreneur">
                                    Entrepreneur
                                </option>
                                <option value="glossary">Glossary</option>
                            </select>
                            <div className="flex justify-between mt-2">
                                <button
                                    type="button"
                                    onClick={() => setModalOpen(false)}
                                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="bg-navy hover:bg-navy-2 text-white px-4 py-2 rounded transition-colors"
                                >
                                    {isEditing ? "Update" : "Create"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FAQ_Admin;
